import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Col, Row } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import { useSearchParams } from 'react-router-dom';
import { forEach, groupBy, isArray, isEmpty, orderBy } from 'lodash';
import { chartColors, publishLatencyChartColors } from '@/utils/constant';
import SearchBar from './SearchBar';
import Chart from './Chart';

// 最大数据数量
const MaxSteps = 300;

// 步调计算，必须30倍数（秒）
const calculateStep = (step) => {
  if (step <= 30) {
    return 30;
  }
  // 倍数取整(下边缘)，可能最终略大于MaxSteps
  const multiple = parseInt(step / 30, 10);
  return multiple * 30;
};

export default function BoardPanel({ getChartsData, colProps, ...others }) {
  const [filter, setFilter] = useState([]);
  const [searchParams] = useSearchParams();
  const clusterName = searchParams.get('name');
  const [properties, setProperties] = useState([]);
  const [chartsData, setChartsData] = useState({});
  const [loading, setLoading] = useState(false);
  const searchRef = useRef();

  const [initialData, setInitialData] = useState(null);

  const loadData = useMemoizedFn(async (range, initProperties) => {
    setFilter(range);
    setChartsData({});
    setLoading(true);
    const start = dayjs(range[0]).unix();
    const end = dayjs(range[1]).unix();
    const timeRangeSeconds = dayjs(range[1]).diff(dayjs(range[0]), 'second');
    const step = parseInt(timeRangeSeconds / MaxSteps, 10);
    const calculatedStep = calculateStep(step);
    try {
      const { data } = await getChartsData({
        start,
        end,
        step: calculatedStep,
      });

      // 如果需要初始化properites
      if (initProperties) {
        setProperties(
          data.properties.map((item) => {
            item.isDisplay = item.defaultDisplay === 'true';
            return item;
          }),
        );
      }
      delete data.properties;
      const _data = {};

      Object.keys(data).map((dataKey) => {
        const filledArray = [];
        const currentArray = data[dataKey];
        // 空不做处理
        if (!isArray(currentArray) || isEmpty(currentArray)) {
          return;
        }

        // 补齐时间，从开始按照步调挨个尝试补齐
        let time = start;
        while (time <= end) {
          // 存在 instance（分组信息）
          if (currentArray[0].instance) {
            const instanceGroup = groupBy(currentArray, 'instance');
            forEach(instanceGroup, (array, instance) => {
              doFillArray(array, time, filledArray, instance);
            });
          } else {
            doFillArray(currentArray, time, filledArray);
          }
          time += calculatedStep;
        }
        // 排序
        _data[dataKey] = orderBy(filledArray, ['instance', 'time'], ['asc', 'asc']);
      });
      setChartsData(_data);
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setLoading(false);
    }
  });

  const onRefresh = useMemoizedFn(() => {
    if (!isEmpty(filter)) {
      loadData(filter);
    }
  });

  const doInitLoad = useMemoizedFn(async () => {
    const now = dayjs().startOf('minute').add(1, 'minute');
    const from = now.subtract(1, 'day');

    const initialData = {
      time: '24h',
      range: [from.toDate(), now.toDate()],
    };
    setInitialData(initialData);
    await loadData(initialData.range, true);
    searchRef.current?.reset(initialData);
  });

  useEffect(() => {
    doInitLoad();
  }, [doInitLoad, clusterName]);

  const filterChange = (range) => {
    loadData(range);
  };

  const getColors = ({ key }) => {
    // publishLatency 延时情况下，特殊颜色
    if (key === 'publishLatency' || key === 'kopTopicPublishLatency') {
      return publishLatencyChartColors;
    }
    return chartColors;
  };

  const onBrush = (range) => {
    searchRef.current?.rangeChange(range);
  };

  return (
    <Root {...others}>
      <SearchBar
        properties={properties}
        changeProperties={setProperties}
        onChange={filterChange}
        onRefresh={onRefresh}
        initialData={initialData}
        ref={searchRef}
        loading={loading}
      />

      <Row gutter={16}>
        {properties.map((item, index) =>
          item.isDisplay ? (
            <Col key={index} xs={12} xl={6} {...colProps}>
              <Chart
                name={item.name}
                dataKey={item.key}
                currentOrder={index}
                chartsData={chartsData}
                colors={getColors(item)}
                color={getColors(item)[index % 3]}
                description={item.description}
                unit={item.unit}
                loading={loading}
                onBrush={onBrush}
                isMultipleInstances={item.isMultipleInstances === 'true'}
              />
            </Col>
          ) : null,
        )}
      </Row>
    </Root>
  );
}

const Root = styled.div`
  overflow-x: hidden;
  margin: 0 -4px;
  padding: 0 4px;
`;

/**
 * Fills an array with missing data points based on a given time, and optionally inserts an instance.
 *
 * @param {Array} array - The array to be filled.
 * @param {number} time - 填充的time.
 * @param {Array} filledArray - The array to fill with missing data points.
 * @param {string|undefined} instance - The instance to insert into the filled array.
 */
function doFillArray(array, time, filledArray, instance) {
  // Find an item in the array with a matching time
  const item = array.find((item) => item.time === time);

  // If there is no matching item
  if (!item) {
    // Create a new object with the given time and a null value
    const param = {};

    forEach(array[0], (_, key) => {
      param[key] = null;
    });
    param.time = time;

    // If an instance was provided, add it to the new object
    if (instance) {
      param.instance = instance;
    }

    // Push the new object into the filled array
    filledArray.push(param);
  } else {
    // If there is a matching item, push a copy of it into the filled array
    filledArray.push({ ...item });
  }
}
